import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import Icon from "../../Icon/Icon";
import { Device } from "../../device";

//Read more (Wrapper with set height, hidden overflow & Read more button.
//height: number of max height pixels.
//threshold: If the content is higher than this number, the Read More gets activated

//Usage:
/*
<ReadMore height={300}>
    --Content here--
</ReadMore>
*/

interface ReadMoreProps
{
    height: number;
    threshold?: number;
}

interface ReadMoreContentStyleProps
{
    showMask: boolean;
    maxHeight?: number;
}

const ReadMoreContent = styled.div<ReadMoreContentStyleProps>`
    overflow: hidden;
    max-height: ${props => props.maxHeight ? props.maxHeight + "px" : "auto"};
    mask-image: ${props => (props.showMask) ?
        "linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0))" :
        "none"};
    transition: max-height .4s cubic-bezier(.165,.84,.44,1);
`;

const ReadMoreBar = styled.div`
    position: relative;
    padding: 1rem 0;
    text-align: center;
    z-index: 0;
`;

const ReadMoreButton = styled.button`
    background-color: transparent;
    border-radius: 50%;
    color: currentColor;
    cursor: pointer;
    display: inline-flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    font-size: var(--text-small, 13px);
    font-weight: bold;
    letter-spacing: 0;
    line-height: 1;
    margin: 0;
    overflow: hidden;
    padding: 0;
    position: relative;
    height: 3rem;
    width: 3rem;
    user-select: none;
    vertical-align: middle;
    text-decoration: none;
    text-transform: uppercase;
    transform-origin: center center;
    transition: transform .4s cubic-bezier(.165,.84,.44,1);
    z-index: 0;
    opacity: .75;
    &:hover {
        opacity: 1;
        transform: rotate(90deg);
    }
    &:focus {
        outline: none;
        transform: scale(.95);
    }
    ${Device.mobileMax} {
        height: 2.5rem;
        width: 2.5rem;
    }
    svg {
        width: 1.25rem;
        height: 1.25rem;
    }
`;

const ReadMore: React.FunctionComponent<ReadMoreProps> = (props) =>
{
    const { height, threshold } = props;

    if(!height || height <= 0)
    {
        return <>{props.children}</>;
    }

    const [getMaxHeight, setMaxHeight] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);

    const getCurrentScrollHeight = useCallback((): number =>
    {
        if (contentRef.current)
        {
            return contentRef.current.scrollHeight;
        }
        return 0;
    }, [contentRef]);

    useEffect(() =>
    {
        const scrollHeight = getCurrentScrollHeight();

        if (scrollHeight)
        {
            const cropHeight = height + (threshold || 100);
            if (scrollHeight > cropHeight)
            {
                setMaxHeight(height);
            }
            else
            {
                setMaxHeight(0);
            }
        }
    }, [height, threshold, getCurrentScrollHeight]);

    const isOpen = (): boolean =>
    {
        return getMaxHeight > 0 && getCurrentScrollHeight() === getMaxHeight;
    };

    const toggleExpanded = () =>
    {
        let maxHeight = getMaxHeight;
        if (isOpen())
        {
            maxHeight = height;
        }
        else if (contentRef.current)
        {
            maxHeight = getCurrentScrollHeight() || maxHeight;
        }
        setMaxHeight(maxHeight);
    };

    return (
        <>
            <ReadMoreContent
                {...props}
                showMask={!isOpen()}
                maxHeight={getMaxHeight}
                ref={contentRef}>
                {props.children}
            </ReadMoreContent>
            {getMaxHeight > 0 ?
                <ReadMoreBar>
                    <ReadMoreButton onClick={toggleExpanded}>
                        {isOpen() ? <Icon name="close" /> : <Icon name="plus" />}
                    </ReadMoreButton>
                </ReadMoreBar>
                : null}
        </>
    );
};

ReadMore.defaultProps = {
    threshold: 100
};

export default ReadMore;